import React, { FunctionComponent, useState } from "react";
import styles from "../CommonStyles/CommonGraphStyles.module.css";
import CountBox from "../Components/CountBox/CountBox";
import { Box, Grid, useMediaQuery, useTheme, Container } from "@mui/material";

/**
 * Props for AnalyticsCountContainer
 *
 * NOTE:
 * - Using `any` here to avoid breaking existing usages.
 * - You can gradually replace `any` with more specific types if needed.
 */
type props = {
  /**
   * Lead stage-wise counts.
   * Expected keys (based on usage):
   *  - FRESH
   *  - CALLBACK
   *  - INTERESTED
   *  - WON
   *  - "NOT INTERESTED"
   *  - LOST
   */
  stage: any;

  /**
   * React Router history object or any navigation object
   * that `CountBox` uses to redirect / navigate.
   */
  history: any;

  /**
   * Selected time filter for counts.
   * - MTD   => Month to Date
   * - YTD   => Year to Date
   * - PM    => Previous Month
   * - All   => All time
   * - CUSTOM => Custom date range
   */
  filter: "MTD" | "YTD" | "PM" | "All" | "CUSTOM";

  /**
   * Task/visit stage-wise counts.
   * Expected keys (based on usage):
   *  - "Completed"
   *  - "Pending"
   */
  taskStage: any;

  /**
   * Filter configuration passed down to CountBox for tasks
   * when used with lead stages.
   */
  taskFilter?: any;

  /**
   * Filter configuration passed down to CountBox for leads.
   */
  leadFilter?: any;

  /**
   * Filter configuration passed down to CountBox for task-related
   * count boxes (Completed / Pending visits).
   */
  otherTaskFilter?: any;

  /**
   * Filter configuration passed down to CountBox for contact-related
   * counts (used with visit boxes).
   */
  otherContactFilter?: any;

  /**
   * Source filter / metadata used in CountBox to further segment data
   * (e.g. campaign source, channel, etc.).
   */
  source: any;

  /**
   * Total number of leads (sum of all lead stages).
   */
  total: any;
};

/**
 * AnalyticsCountContainer
 *
 * This component renders a grid of CountBox components that display:
 *  - Total lead count
 *  - Lead counts by stage (Fresh, Call Back, Interested, Closed Won, Not Interested, Closed Lost)
 *  - Task/visit counts (Completed Visits, Scheduled Visits)
 *
 * All navigation and filtering logic is delegated to the CountBox component
 * via props such as `history`, `filter`, `taskFilter`, `leadFilter`, etc.
 */
const AnalyticsCountContainer: FunctionComponent<props> = ({
  stage,
  history,
  filter,
  taskStage,
  taskFilter,
  leadFilter,
  otherTaskFilter,
  otherContactFilter,
  source,
  total,
}) => {
  // Access the MUI theme (for responsive breakpoints, colors, etc.)
  const themeMUI = useTheme();

  // Check if current viewport size is mobile (small and below)
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // Currently, `isMobile` is not used for conditional rendering,
  // but it can be used in the future to change layout / styling.

  return (
    <>
      {/* Container for all count boxes, styled via CSS module */}
      <div className={styles.countBoxGrid}>
        {/* Total leads across all stages */}
        <CountBox
          count={total}
          text="Total Leads"
          style={{ color: "rgb(213, 30, 30)" }}
          history={history}
          filterText="" // No specific stage filter applied
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Fresh leads */}
        <CountBox
          count={stage.FRESH}
          text="Fresh"
          style={{ color: "rgba(255, 99, 132, 1)" }}
          history={history}
          filterText="FRESH"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Leads that require callback */}
        <CountBox
          count={stage.CALLBACK}
          text="Call Back"
          style={{ color: "rgba(54, 162, 235, 1)" }}
          history={history}
          filterText="CALLBACK"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Interested leads */}
        <CountBox
          count={stage.INTERESTED}
          text="Interested"
          style={{ color: "rgba(255, 206, 86, 1)" }}
          history={history}
          filterText="INTERESTED"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Closed Won leads */}
        <CountBox
          count={stage.WON}
          text="Closed Won"
          style={{ color: "rgba(75, 192, 192, 1)" }}
          history={history}
          filterText="WON"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Leads marked as Not Interested */}
        <CountBox
          count={stage["NOT INTERESTED"]}
          text="Not Interested"
          style={{ color: "rgba(153, 102, 255, 1)" }}
          history={history}
          filterText="NOT INTERESTED"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Closed Lost leads */}
        <CountBox
          count={stage.LOST}
          text="Closed Lost"
          style={{ color: "rgba(255, 159, 64, 1)" }}
          history={history}
          filterText="LOST"
          filter={filter}
          taskFilter={taskFilter}
          leadFilter={leadFilter}
          source={source}
        />

        {/* Completed visits (tasks) */}
        <CountBox
          count={taskStage["Completed"]}
          text="Completed Visits"
          style={{ color: "#aaf0d1" }}
          history={history}
          filterText="Completed"
          filter={filter}
          taskFilter={otherTaskFilter}
          leadFilter={otherContactFilter}
          source={source}
        />

        {/* Scheduled (pending) visits (tasks) */}
        <CountBox
          count={taskStage["Pending"]}
          text="Scheduled Visits"
          style={{ color: "#2FA2D3" }}
          history={history}
          filterText="Pending"
          filter={filter}
          taskFilter={otherTaskFilter}
          leadFilter={otherContactFilter}
          source={source}
        />
      </div>
    </>
  );
};

export default AnalyticsCountContainer;
